import React, { useState, useEffect, useRef } from "react";
import "./style.sass";

export const ClockTimePicker = ({
  isOpen,
  initialTime = "",
  onClose,
  onSelectTime,
}) => {
  const [hour, setHour] = useState(10);
  const [minute, setMinute] = useState(0);
  const [period, setPeriod] = useState("AM");
  const [activeTab, setActiveTab] = useState("presets"); // 'presets' | 'custom'

  const [hourStr, setHourStr] = useState("10");
  const [minuteStr, setMinuteStr] = useState("00");

  const hourInputRef = useRef(null);
  const minuteInputRef = useRef(null);

  // Parse initial time when modal opens
  useEffect(() => {
    if (!isOpen) return;
    if (!initialTime) {
      setHour(10);
      setMinute(0);
      setPeriod("AM");
      setHourStr("10");
      setMinuteStr("00");
      return;
    }

    const clean = initialTime.trim();
    const isPm = clean.toUpperCase().includes("PM");
    const isAm = clean.toUpperCase().includes("AM");

    const numPart = clean.replace(/AM|PM/gi, "").trim();
    const parts = numPart.split(":");

    let parsedHour = parseInt(parts[0], 10);
    let parsedMinute = parts[1] ? parseInt(parts[1], 10) : 0;

    if (isNaN(parsedHour)) parsedHour = 10;
    if (isNaN(parsedMinute)) parsedMinute = 0;

    let finalH = parsedHour;
    let finalP = "AM";

    if (isPm || isAm) {
      finalP = isPm ? "PM" : "AM";
      finalH = parsedHour > 12 ? parsedHour - 12 : parsedHour === 0 ? 12 : parsedHour;
    } else {
      if (parsedHour >= 12) {
        finalP = "PM";
        finalH = parsedHour === 12 ? 12 : parsedHour - 12;
      } else {
        finalP = "AM";
        finalH = parsedHour === 0 ? 12 : parsedHour;
      }
    }

    setHour(finalH);
    setMinute(parsedMinute);
    setPeriod(finalP);
    setHourStr(String(finalH).padStart(2, "0"));
    setMinuteStr(String(parsedMinute).padStart(2, "0"));
  }, [isOpen, initialTime]);

  // Sync string representations when hour/minute updates
  useEffect(() => {
    setHourStr(String(hour).padStart(2, "0"));
  }, [hour]);

  useEffect(() => {
    setMinuteStr(String(minute).padStart(2, "0"));
  }, [minute]);

  if (!isOpen) return null;

  const hoursList = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const minutesList = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  const presets = [
    { label: "09:00 AM", h: 9, m: 0, p: "AM" },
    { label: "10:00 AM", h: 10, m: 0, p: "AM" },
    { label: "11:30 AM", h: 11, m: 30, p: "AM" },
    { label: "02:00 PM", h: 2, m: 0, p: "PM" },
    { label: "04:30 PM", h: 4, m: 30, p: "PM" },
    { label: "06:00 PM", h: 6, m: 0, p: "PM" },
  ];

  const applyPreset = (p) => {
    setHour(p.h);
    setMinute(p.m);
    setPeriod(p.p);
  };

  const isPresetSelected = (p) => {
    return hour === p.h && minute === p.m && period === p.p;
  };

  // Manual typing handlers
  const handleHourInputChange = (e) => {
    const val = e.target.value.replace(/\D/g, "");
    if (val.length > 2) return;
    setHourStr(val);

    const num = parseInt(val, 10);
    if (!isNaN(num)) {
      if (num >= 1 && num <= 12) {
        setHour(num);
      }
    }

    if (val.length === 2) {
      if (minuteInputRef.current) {
        minuteInputRef.current.focus();
        minuteInputRef.current.select();
      }
    }
  };

  const handleHourBlur = () => {
    let num = parseInt(hourStr, 10);
    if (isNaN(num) || num < 1) num = 12;
    if (num > 12) num = 12;
    setHour(num);
    setHourStr(String(num).padStart(2, "0"));
  };

  const handleMinuteInputChange = (e) => {
    const val = e.target.value.replace(/\D/g, "");
    if (val.length > 2) return;
    setMinuteStr(val);

    const num = parseInt(val, 10);
    if (!isNaN(num)) {
      if (num >= 0 && num <= 59) {
        setMinute(num);
      }
    }
  };

  const handleMinuteBlur = () => {
    let num = parseInt(minuteStr, 10);
    if (isNaN(num) || num < 0) num = 0;
    if (num > 59) num = 59;
    setMinute(num);
    setMinuteStr(String(num).padStart(2, "0"));
  };

  const handleApply = () => {
    let h24 = hour;
    if (period === "PM" && hour < 12) h24 = hour + 12;
    if (period === "AM" && hour === 12) h24 = 0;

    const formattedHour24 = String(h24).padStart(2, "0");
    const formattedMinute = String(minute).padStart(2, "0");
    const formatted24h = `${formattedHour24}:${formattedMinute}`;

    const formatted12hHour = String(hour).padStart(2, "0");
    const displayString = `${formatted12hHour}:${formattedMinute} ${period}`;

    onSelectTime(formatted24h, displayString);
    onClose();
  };

  return (
    <div className="clock_picker_overlay" onClick={onClose}>
      <div
        className="clock_picker_card_minimal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="minimal_header">
          <div className="title_area">
            <h3>Select Time</h3>
            <p>Type digits or choose preset</p>
          </div>
          <button
            type="button"
            className="close_btn"
            onClick={onClose}
            title="Close"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Digital Time Display Banner with Editable Inputs */}
        <div className="time_display_card">
          <div className="time_digits">
            <input
              ref={hourInputRef}
              type="text"
              className="digit_input"
              value={hourStr}
              onChange={handleHourInputChange}
              onBlur={handleHourBlur}
              onFocus={(e) => e.target.select()}
              maxLength={2}
              title="Click to type hour (1-12)"
            />
            <span className="colon">:</span>
            <input
              ref={minuteInputRef}
              type="text"
              className="digit_input"
              value={minuteStr}
              onChange={handleMinuteInputChange}
              onBlur={handleMinuteBlur}
              onFocus={(e) => e.target.select()}
              maxLength={2}
              title="Click to type minute (00-59)"
            />
          </div>

          <div className="ampm_selector">
            <button
              type="button"
              className={`ampm_btn ${period === "AM" ? "active" : ""}`}
              onClick={() => setPeriod("AM")}
            >
              AM
            </button>
            <button
              type="button"
              className={`ampm_btn ${period === "PM" ? "active" : ""}`}
              onClick={() => setPeriod("PM")}
            >
              PM
            </button>
          </div>
        </div>

        {/* Tabs: Quick Presets vs Custom Picker */}
        <div className="tab_selector">
          <button
            type="button"
            className={`tab_btn ${activeTab === "presets" ? "active" : ""}`}
            onClick={() => setActiveTab("presets")}
          >
            Quick Presets
          </button>
          <button
            type="button"
            className={`tab_btn ${activeTab === "custom" ? "active" : ""}`}
            onClick={() => setActiveTab("custom")}
          >
            Custom Time
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "presets" ? (
          <div className="presets_grid">
            {presets.map((p, idx) => {
              const selected = isPresetSelected(p);
              return (
                <button
                  key={idx}
                  type="button"
                  className={`preset_pill ${selected ? "selected" : ""}`}
                  onClick={() => applyPreset(p)}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="custom_picker_grid">
            {/* Hours selection */}
            <div className="selector_column">
              <label>Hour</label>
              <div className="chips_grid">
                {hoursList.map((h) => (
                  <button
                    key={h}
                    type="button"
                    className={`chip_btn ${hour === h ? "selected" : ""}`}
                    onClick={() => setHour(h)}
                  >
                    {String(h).padStart(2, "0")}
                  </button>
                ))}
              </div>
            </div>

            {/* Minutes selection */}
            <div className="selector_column">
              <label>Minute</label>
              <div className="chips_grid">
                {minutesList.map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={`chip_btn ${minute === m ? "selected" : ""}`}
                    onClick={() => setMinute(m)}
                  >
                    {String(m).padStart(2, "0")}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="minimal_footer">
          <button
            type="button"
            className="cancel_btn"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="set_time_btn"
            onClick={handleApply}
          >
            Set Time
          </button>
        </div>
      </div>
    </div>
  );
};
